import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  Text,
  Image,
  useColorModeValue,
  Box,
  Progress // Added Progress
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { facts as megalithicFacts } from '../../data/megalithicFacts'; // Import facts

// Define keyframes for a subtle spinning animation if using an image
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const AdventureJournalLoading = ({ isOpen }) => {
  const bgColor = useColorModeValue('rgba(245, 240, 230, 0.95)', 'rgba(40, 35, 30, 0.95)');
  const textColor = useColorModeValue('gray.800', 'gray.800'); // Changed to dark gray/black for both modes
  const borderColor = useColorModeValue('yellow.700', 'yellow.400');
  const progressColorScheme = useColorModeValue('yellow', 'orange'); // Thematic color for progress bar

  const [currentFact, setCurrentFact] = useState(''); // Changed from currentFactData to currentFact (string)
  const [progressValue, setProgressValue] = useState(0);

  // Select and set a random fact when the modal opens and cycle every 10 seconds
  useEffect(() => {
    let factInterval;

    const setNewFact = () => {
      if (megalithicFacts.length > 0) {
        const randomFactText = megalithicFacts[Math.floor(Math.random() * megalithicFacts.length)];
        setCurrentFact(randomFactText); // Set the string directly
      }
    };

    if (isOpen) {
      setNewFact(); // Set initial fact
      setProgressValue(0); // Reset progress when modal opens
      factInterval = setInterval(setNewFact, 10000); // Cycle fact every 10 seconds
    } else {
      setCurrentFact(''); // Clear fact when modal closes
      clearInterval(factInterval);
    }

    return () => {
      clearInterval(factInterval); // Cleanup interval on unmount or when isOpen changes
    };
  }, [isOpen]);

  // Effect for the progress bar timer
  useEffect(() => {
    let timer;
    if (isOpen) {
      const totalDuration = 120000; // 2 minutes in milliseconds
      const intervalTime = 100; // Update every 100ms
      const increment = (intervalTime / totalDuration) * 100;

      timer = setInterval(() => {
        setProgressValue(prev => {
          if (prev >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prev + increment;
        });
      }, intervalTime);
    }
    return () => {
      clearInterval(timer);
    };
  }, [isOpen]);


  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg={bgColor}
        backgroundImage="url('/textures/parchment.png')"
        backgroundRepeat="no-repeat" // Changed to no-repeat, cover should handle it
        backgroundSize="cover" // Changed to cover for better fit
        borderRadius="lg"
        boxShadow="0 10px 30px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.1)" // More pronounced shadow
        border="3px solid" // Thicker border
        borderColor={borderColor}
        maxWidth="380px"
        py={10} // Increased padding
        px={6}
      >
        <ModalBody>
          <VStack spacing={8} textAlign="center"> {/* Increased spacing */}
            <Image
              src="/textures/compass-rose.png"
              alt="Loading..."
              boxSize="120px" // Increased size
              animation={`${spin} 2.5s linear infinite`}
              filter="drop-shadow(0px 5px 10px rgba(0,0,0,0.2))"
            />
            <Text fontSize="md" fontWeight="normal" color={textColor} fontFamily="body" px={4} lineHeight="tall">
              {currentFact}
            </Text>
            <Progress
              value={progressValue}
              size="sm"
              colorScheme={progressColorScheme}
              width="80%"
              borderRadius="md"
              mt={4} // Added margin top
              hasStripe
              isAnimated={progressValue < 100} // Animate only while loading
            />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AdventureJournalLoading;
